import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import DashboardLayout from '../../components/DashboardLayout'
import { assessmentService, courseService } from '../../api'
import {
  ArrowLeft,
  BookOpen,
  Calendar,
  ClipboardCheck,
  Clock,
  FileText,
  HelpCircle,
  Layers,
  Save,
  Sparkles,
  Trash2,
} from 'lucide-react'
import toast from 'react-hot-toast'
import AppSelect from '../../components/ui/AppSelect'
import '../../styles/assessment-form.css'

const ASSESSMENT_TYPES = [
  { value: 'exam', label: 'Exam', icon: FileText },
  { value: 'test', label: 'Test', icon: ClipboardCheck },
  { value: 'quiz', label: 'Quiz', icon: HelpCircle },
  { value: 'assignment', label: 'Assignment', icon: Layers },
]

function AdminAssessmentDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEdit = Boolean(id)

  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [courses, setCourses] = useState([])
  const [subjects, setSubjects] = useState([])
  const [tieToCourse, setTieToCourse] = useState(true)

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assessment_type: 'test',
    course: '',
    subject: '',
    time_limit_minutes: 60,
    max_attempts: 1,
    passing_score: 70,
    start_date: '',
    end_date: '',
    is_active: true,
  })

  useEffect(() => {
    fetchInitialData()
  }, [])

  const fetchInitialData = async () => {
    try {
      const coursesRes = await courseService.getCourses()
      setCourses(coursesRes.data.results || coursesRes.data)

      if (isEdit) {
        const assessRes = await assessmentService.getAssessmentDetail(id)
        const data = assessRes.data
        const hasCourse = Boolean(data.course)
        setTieToCourse(hasCourse)
        setFormData({
          title: data.title || '',
          description: data.description || '',
          assessment_type: data.assessment_type || 'test',
          course: data.course || '',
          subject: data.subject || '',
          time_limit_minutes: data.time_limit_minutes ?? 60,
          max_attempts: data.max_attempts ?? 1,
          passing_score: data.passing_score ?? 70,
          start_date: data.start_date ? new Date(data.start_date).toISOString().slice(0, 16) : '',
          end_date: data.end_date ? new Date(data.end_date).toISOString().slice(0, 16) : '',
          is_active: data.is_active !== false,
        })

        if (data.course) {
          const subjectsRes = await courseService.getSubjects({ course: data.course })
          setSubjects(subjectsRes.data.results || subjectsRes.data)
        }
      }
    } catch {
      toast.error('Failed to load assessment data')
    } finally {
      setLoading(false)
    }
  }

  const handleCourseChange = async (courseId) => {
    setFormData((prev) => ({ ...prev, course: courseId, subject: '' }))
    if (!courseId) {
      setSubjects([])
      return
    }
    try {
      const res = await courseService.getSubjects({ course: courseId })
      setSubjects(res.data.results || res.data)
    } catch {
      toast.error('Failed to load subjects')
    }
  }

  const handleModeChange = (nextTied) => {
    setTieToCourse(nextTied)
    if (!nextTied) {
      setFormData((prev) => ({ ...prev, course: '', subject: '' }))
      setSubjects([])
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (tieToCourse && !formData.course) {
      toast.error('Select a course, or switch to practice mode')
      return
    }
    if (!formData.start_date || !formData.end_date) {
      toast.error('Set both the start and end date')
      return
    }

    setSaving(true)
    try {
      const payload = {
        ...formData,
        course: tieToCourse ? formData.course : null,
        subject: tieToCourse && formData.subject ? formData.subject : null,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: new Date(formData.end_date).toISOString(),
      }

      if (isEdit) {
        await assessmentService.updateAssessment(id, payload)
        toast.success('Assessment updated')
        navigate('/admin/assessments')
      } else {
        const res = await assessmentService.createAssessment(payload)
        const createdId = res.data?.id || res.data?.assessment?.id
        toast.success('Assessment created')
        navigate(createdId ? `/admin/assessments/${createdId}/questions` : '/admin/assessments')
      }
    } catch (err) {
      const errors = err.response?.data?.errors || err.response?.data
      const firstError = errors && typeof errors === 'object'
        ? Object.values(errors).flat?.()?.[0] || err.response?.data?.detail
        : err.response?.data?.detail
      toast.error(firstError || 'Failed to save assessment')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm('Delete this assessment? This cannot be undone.')) return
    try {
      await assessmentService.deleteAssessment(id)
      toast.success('Assessment deleted')
      navigate('/admin/assessments')
    } catch {
      toast.error('Failed to delete assessment')
    }
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-[calc(100vh-80px)]">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <main className="page-container content-width assessment-editor-page">
        <button
          type="button"
          onClick={() => navigate('/admin/assessments')}
          className="assessment-editor-back"
        >
          <ArrowLeft size={16} />
          Back to assessments
        </button>

        <div className="assessment-editor-shell">
          <header className="assessment-editor-header">
            <div className="assessment-editor-heading">
              <div className="assessment-editor-heading-icon">
                <ClipboardCheck size={22} />
              </div>
              <div>
                <p className="assessment-editor-eyebrow">Assessment workspace</p>
                <h1>{isEdit ? 'Edit Assessment' : 'New Assessment'}</h1>
                <p>Set evaluation rules for course certification or standalone practice.</p>
              </div>
            </div>
            <div className="flex items-center gap-md">
              {isEdit && (
                <button type="button" className="assessment-delete-btn" onClick={handleDelete}>
                  <Trash2 size={16} />
                  Delete
                </button>
              )}
              <div className={`assessment-editor-status${formData.is_active ? ' is-active' : ''}`}>
                {formData.is_active ? 'Active' : 'Hidden'}
              </div>
            </div>
          </header>

          <form onSubmit={handleSubmit} className="assessment-editor-form">
            <section className="assessment-editor-section">
              <div className="assessment-editor-section-heading">
                <span>01</span>
                <div>
                  <h2>Purpose and placement</h2>
                  <p>
                    Course-tied assessments block certificates until passed.
                    Practice assessments stay available for ad-hoc testing.
                  </p>
                </div>
              </div>

              <div className="assessment-mode-cards" role="group" aria-label="Assessment placement">
                <button
                  type="button"
                  className={`assessment-mode-card${tieToCourse ? ' is-active' : ''}`}
                  onClick={() => handleModeChange(true)}
                >
                  <strong>Course requirement</strong>
                  <small>Counts toward course completion and certification, like Coursera graded work.</small>
                </button>
                <button
                  type="button"
                  className={`assessment-mode-card${!tieToCourse ? ' is-active' : ''}`}
                  onClick={() => handleModeChange(false)}
                >
                  <strong>Standalone practice</strong>
                  <small>Not tied to a course. Useful for corporate drills or bootcamp warm-ups.</small>
                </button>
              </div>

              <label className="assessment-editor-field">
                <span>Assessment title</span>
                <input
                  required
                  type="text"
                  placeholder="e.g. Final Python Checkpoint"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                />
              </label>

              <label className="assessment-editor-field">
                <span>Instructions</span>
                <textarea
                  rows={4}
                  placeholder="Tell learners what to expect, how long they have, and how scoring works..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </label>

              {tieToCourse && (
                <div className="assessment-editor-field-grid">
                  <div className="assessment-editor-field">
                    <label htmlFor="assessment-course">Course</label>
                    <AppSelect
                      id="assessment-course"
                      required
                      className="assessment-editor-select"
                      value={formData.course}
                      onChange={(e) => handleCourseChange(e.target.value)}
                    >
                      <option value="">Select a course</option>
                      {courses.map((course) => (
                        <option key={course.id} value={course.id}>{course.title}</option>
                      ))}
                    </AppSelect>
                  </div>

                  <div className="assessment-editor-field">
                    <label htmlFor="assessment-subject">Subject (optional)</label>
                    <AppSelect
                      id="assessment-subject"
                      className="assessment-editor-select"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      disabled={!formData.course}
                    >
                      <option value="">Whole course</option>
                      {subjects.map((subject) => (
                        <option key={subject.id} value={subject.id}>{subject.title}</option>
                      ))}
                    </AppSelect>
                  </div>
                </div>
              )}

              {!tieToCourse && (
                <div className="assessment-completion-banner">
                  <h4>
                    <Sparkles size={16} style={{ display: 'inline', marginRight: 6 }} />
                    Practice mode
                  </h4>
                  <p>
                    This assessment will not block course completion or certificates.
                    Learners in your campus can still take it for practice.
                  </p>
                </div>
              )}
            </section>

            <section className="assessment-editor-section">
              <div className="assessment-editor-section-heading">
                <span>02</span>
                <div>
                  <h2>Type and visibility</h2>
                  <p>Choose the evaluation format and whether learners can see it now.</p>
                </div>
              </div>

              <div className="assessment-editor-field">
                <span>Assessment type</span>
                <div className="assessment-type-grid" role="group" aria-label="Assessment type">
                  {ASSESSMENT_TYPES.map(({ value, label, icon: Icon }) => (
                    <button
                      key={value}
                      type="button"
                      className={`assessment-type-chip${formData.assessment_type === value ? ' is-active' : ''}`}
                      onClick={() => setFormData({ ...formData, assessment_type: value })}
                      aria-pressed={formData.assessment_type === value}
                    >
                      <Icon size={18} aria-hidden="true" />
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <label className="assessment-access-toggle">
                <span className="assessment-access-toggle-copy">
                  <strong>Visible to learners</strong>
                  <small>Hidden assessments stay in the admin catalog but are not available to take.</small>
                </span>
                <span className="assessment-toggle">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  <span aria-hidden="true" />
                </span>
              </label>
            </section>

            <section className="assessment-editor-section">
              <div className="assessment-editor-section-heading">
                <span>03</span>
                <div>
                  <h2>Timing and scoring</h2>
                  <p>Set the window, attempts, duration, and the score needed to pass.</p>
                </div>
              </div>

              <div className="assessment-editor-field-grid">
                <label className="assessment-editor-field">
                  <span><Clock size={14} /> Duration (minutes)</span>
                  <input
                    required
                    type="number"
                    min="1"
                    value={formData.time_limit_minutes}
                    onChange={(e) => setFormData({ ...formData, time_limit_minutes: Number(e.target.value) })}
                  />
                </label>

                <label className="assessment-editor-field">
                  <span><HelpCircle size={14} /> Max attempts</span>
                  <input
                    required
                    type="number"
                    min="1"
                    value={formData.max_attempts}
                    onChange={(e) => setFormData({ ...formData, max_attempts: Number(e.target.value) })}
                  />
                </label>

                <label className="assessment-editor-field">
                  <span><BookOpen size={14} /> Passing score (%)</span>
                  <input
                    required
                    type="number"
                    min="1"
                    max="100"
                    value={formData.passing_score}
                    onChange={(e) => setFormData({ ...formData, passing_score: Number(e.target.value) })}
                  />
                </label>

                <div className="assessment-editor-field">
                  <span>Quick tip</span>
                  <p style={{ margin: 0, color: 'var(--color-text-secondary)', fontSize: '0.8rem', lineHeight: 1.45 }}>
                    {tieToCourse
                      ? 'Learners must reach this pass mark before the course can be marked complete.'
                      : 'Practice scores are recorded, but they never gate certificates.'}
                  </p>
                </div>

                <label className="assessment-editor-field">
                  <span><Calendar size={14} /> Opens</span>
                  <input
                    required
                    type="datetime-local"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  />
                </label>

                <label className="assessment-editor-field">
                  <span><Calendar size={14} /> Closes</span>
                  <input
                    required
                    type="datetime-local"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  />
                </label>
              </div>
            </section>

            <footer className="assessment-editor-footer">
              <p>
                {tieToCourse
                  ? 'Course-tied assessments become required for completion and certificates.'
                  : 'Standalone practice assessments stay outside the certification path.'}
              </p>
              <div className="assessment-editor-footer-actions">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => navigate('/admin/assessments')}
                >
                  Discard
                </button>
                <button type="submit" disabled={saving} className="btn btn-primary">
                  {saving ? (
                    <>
                      <span className="assessment-editor-spinner" aria-hidden="true" />
                      Saving…
                    </>
                  ) : (
                    <>
                      <Save size={18} />
                      {isEdit ? 'Update Assessment' : 'Save & Continue'}
                    </>
                  )}
                </button>
              </div>
            </footer>
          </form>
        </div>
      </main>
    </DashboardLayout>
  )
}

export default AdminAssessmentDetail
